import { useState, useEffect, MutableRefObject, useRef } from "react";
import Sketch from "@arcgis/core/widgets/Sketch";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { LayerFieldDefinition } from "../../queries/maps";
import Graphic from "@arcgis/core/Graphic";
import _ from "lodash";
import { graphicIndex } from "../../components/admin/MapManagementEdit";
import { getHoverSymbolForGeometryType, getSymbolForGeometryType } from "../../utils/arcgisHelper";
import { incrementName } from "../../../../utils/incrementName";

export const enum SketchTool {
  Select = "select",
  Edit = "edit",
  Polygon = "polygon",
  Rectangle = "rectangle",
  Circle = "circle",
}

export interface FeatureTracking {
  [graphicId: string]: {
    featureId?: number;
    isNew?: boolean;
    isModified?: boolean;
    isDeleted?: boolean;
    originalGeometry?: any;
  };
}

interface UseSketchEventsOptions {
  sketchRef: MutableRefObject<Sketch | null>;
  sketchInitialized: boolean;
  editLayerRef: MutableRefObject<GraphicsLayer | null>;
  fieldDefinitions?: LayerFieldDefinition[];
  handleAddColumn: (name: string) => LayerFieldDefinition;
  markFeatureAsChanged: ((graphicId: string) => void) | null;
  markFeatureAsChangedDebounced: ((graphicId: string) => void) | null;
}

export function useSketchEvents({
  sketchRef,
  sketchInitialized,
  editLayerRef,
  fieldDefinitions = [],
  handleAddColumn,
  markFeatureAsChanged,
  markFeatureAsChangedDebounced,
}: UseSketchEventsOptions) {
  const [featureTracking, setFeatureTracking] = useState<FeatureTracking>({});
  const [selectedFeatures, setSelectedFeatures] = useState<Graphic[]>([]);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [fieldProperties, setFieldProperties] = useState<{ [key: string]: any }>({});
  const [selectedTool, setSelectedTool] = useState<SketchTool>(SketchTool.Select);
  const [undoableActions, setUndoableActions] = useState(0); // Sketch doesn't keep track of canUndo or canRedo
  const [redoableActions, setRedoableActions] = useState(0);

  const [showFeaturePopup, setShowFeaturePopup] = useState(false);
  const maxFeatureIndex = useRef(0);

  const resetGraphicSymbol = (previousFeatureId: string | null, currentFeatureId: string | null) => {
    // Restore previous selected feature's original symbol
    if (previousFeatureId && graphicIndex.has(previousFeatureId) && previousFeatureId !== currentFeatureId) {
      const prevGraphic = editLayerRef.current?.graphics.at(graphicIndex.get(previousFeatureId)!);
      if (prevGraphic?.geometry && prevGraphic.attributes.graphicId === previousFeatureId) {
        prevGraphic.symbol = getSymbolForGeometryType(prevGraphic.geometry.type);
      }
    }
  };

  type SketchEventHandler = { remove: () => void } | null;
  const [sketchHandlers, setSketchHandlers] = useState<{
    update: SketchEventHandler | null;
    create: SketchEventHandler | null;
    delete: SketchEventHandler | null;
    updateSelection: SketchEventHandler | null;
    undo: SketchEventHandler | null;
    redo: SketchEventHandler | null;
  }>({
    update: null,
    create: null,
    delete: null,
    updateSelection: null,
    undo: null,
    redo: null,
  });

  // Set up sketch event handlers
  const readdHandlers = (selectedTool: SketchTool) => {
    if (sketchRef.current == null) return [];

    _.values(sketchHandlers).forEach((handler) => handler?.remove());

    const updateHandler = sketchRef.current.on("update", handleSketchUpdate);
    const createHandler = sketchRef.current.on("create", handleSketchCreate);
    const deleteHandler = sketchRef.current.on("delete", handleSketchDelete);
    const updateSelectionHandler = sketchRef.current.on(
      "update",
      handleSketchUpdateSelection(selectedTool, selectedFeatureId),
    );
    const undoHandler = sketchRef.current.on("undo", handleSketchUndo);
    const redoHandler = sketchRef.current.on("redo", handleSketchRedo);

    setSketchHandlers({
      update: updateHandler,
      create: createHandler,
      delete: deleteHandler,
      updateSelection: updateSelectionHandler,
      undo: undoHandler,
      redo: redoHandler,
    });

    return [updateHandler, createHandler, deleteHandler, updateSelectionHandler];
  };
  useEffect(() => {
    const handlers = readdHandlers(selectedTool);

    return () => {
      handlers.forEach((handler) => handler?.remove());
    };
  }, [fieldDefinitions, sketchInitialized, !!editLayerRef.current, selectedTool, selectedFeatureId, fieldProperties]);

  // Update fieldProperties when selectedFeatureId changes
  useEffect(() => {
    if (!selectedFeatureId || !editLayerRef.current) return;
    const graphics = editLayerRef.current.graphics;
    if (!graphicIndex.has(selectedFeatureId)) return;
    const selectedGraphic = graphics.at(graphicIndex.get(selectedFeatureId)!);
    if (selectedGraphic) setFieldProperties(selectedGraphic.attributes || {});
  }, [selectedFeatureId]);

  // Event handlers
  const handleSketchCreate: __esri.SketchCreateEventHandler = (event) => {
    if (event.state !== "complete") return;

    // Generate a unique ID for the graphic
    const graphicId = `new-${crypto.randomUUID()}`;

    // Find the primary field to set default value
    const primaryField = fieldDefinitions.find((field) => field.isPrimary) ?? handleAddColumn("Name");

    const featureName = incrementName(
      "New Feature",
      editLayerRef.current?.graphics || [],
      (g) => g.attributes[primaryField!.customField.id] || "",
    );

    // Set default attributes for the new graphic
    event.graphic.attributes = {
      ...(event.graphic.attributes || {}),
      graphicId,
      [primaryField!.customField.id]: featureName,
      index: maxFeatureIndex.current,
    };
    maxFeatureIndex.current += 1;
    if (event.graphic.geometry) event.graphic.symbol = getSymbolForGeometryType(event.graphic.geometry.type);

    // Add to feature tracking
    setFeatureTracking((prev) => ({
      ...prev,
      [graphicId]: {
        isNew: true,
      },
    }));

    setSelectedTool(SketchTool.Edit);
    setSelectedFeatureId(graphicId);
    setFieldProperties(event.graphic.attributes || {});
    markFeatureAsChanged?.(graphicId);
  };

  const handleSketchUpdate: __esri.SketchUpdateEventHandler = (event) => {
    if (event.state !== "complete") return;
    // Update tracking for each modified graphic
    event.graphics.forEach((graphic) => {
      const graphicId = graphic.attributes?.graphicId;
      const featureId = graphic.attributes?.featureId;
      if (!graphicId) return;
      markFeatureAsChanged?.(graphicId);
      setUndoableActions(0);
      setRedoableActions(0);
      event.graphics.forEach((g) => {
        if (g.geometry) g.symbol = getSymbolForGeometryType(g.geometry.type);
      });

      setFeatureTracking((prev) => {
        const currentTracking = prev[graphicId] || {};
        if (!featureId || currentTracking.isNew) return prev;

        // If it's an existing feature (not new), mark as modified
        return {
          ...prev,
          [graphicId]: {
            ...currentTracking,
            featureId,
            isModified: true,
          },
        };
      });
    });
  };

  const handleSketchUpdateSelection: (
    selectedTool: SketchTool,
    previousSelectedFeatureId: string | null,
  ) => __esri.SketchUpdateEventHandler =
    (selectedTool: SketchTool, previousSelectedFeatureId: string | null) => async (event) => {
      switch (event.state) {
        case "start":
          setSelectedFeatures(event.graphics);
          if (selectedTool === SketchTool.Select) sketchRef.current?.cancel();
          if (event.graphics.length !== 1) {
            setSelectedFeatureId(null);
            setFieldProperties({});
            return;
          }
          const graphic = event.graphics[0];
          if (graphic.geometry) {
            graphic.symbol = getHoverSymbolForGeometryType(graphic.geometry.type);
            // Restore previous selected feature's original symbol
            resetGraphicSymbol(previousSelectedFeatureId, graphic.attributes?.graphicId);
          }
          setSelectedFeatureId(graphic.attributes?.graphicId);
          setFieldProperties(graphic.attributes || {});
          setShowFeaturePopup(true);
          setSelectedFeatures([graphic]);
          break;
        case "active":
          const finishedEvents = [
            "move-stop",
            "reshape-stop",
            "scale-stop",
            "rotate-stop",
            "vertex-add",
            "vertex-remove",
          ];
          if (finishedEvents.includes(event.toolEventInfo.type)) {
            setUndoableActions((prev) => prev + 1);
          } else if (event.toolEventInfo.type === "selection-change") {
            const eventInfo = event.toolEventInfo;
            const newGraphicId = eventInfo.added[eventInfo.added.length - 1]?.attributes.graphicId;
            sketchRef.current?.complete();
            if (!graphicIndex.has(newGraphicId)) return;
            const newSelectedGraphic = editLayerRef.current?.graphics.at(graphicIndex.get(newGraphicId)!);
            if (!newSelectedGraphic) return;
            setSelectedFeatureId(newGraphicId);
            setSelectedFeatures([newSelectedGraphic]);
            setFieldProperties(newSelectedGraphic.attributes || {});
            setShowFeaturePopup(true);
            sketchRef.current?.update([newSelectedGraphic]);
          }
          break;
        case "complete":
          // Update attributes of the selected graphic(s)
          event.graphics.forEach((graphic) => {
            if (graphic.attributes?.graphicId === selectedFeatureId) {
              // Merge fieldProperties with existing attributes
              graphic.attributes = {
                ...graphic.attributes,
                ...fieldProperties,
              };
            }
          });
          setSelectedFeatures([]);
          setSelectedFeatureId(null);
          setFieldProperties({});
          setShowFeaturePopup(false);
          break;
      }
    };

  const handleSketchDelete: __esri.SketchDeleteEventHandler = (event) => {
    // Mark deleted features in tracking
    event.graphics.forEach((graphic) => {
      const graphicId = graphic.attributes?.graphicId;
      const featureId = graphic.attributes?.featureId;
      if (!graphicId) return;
      markFeatureAsChanged?.(graphicId);
      setFeatureTracking((prev) => {
        const currentTracking = prev[graphicId] || {};

        // If it's a new feature (not yet saved), just remove from tracking
        if (currentTracking.isNew) {
          const newTracking = { ...prev };
          delete newTracking[graphicId];
          return newTracking;
        }

        if (!featureId) return prev;
        // If it's an existing feature, mark as deleted
        return {
          ...prev,
          [graphicId]: {
            ...currentTracking,
            featureId,
            isDeleted: true,
          },
        };
      });
    });

    // Clear selection
    setSelectedFeatureId(null);
    setFieldProperties({});
  };

  const handleSketchUndo: __esri.SketchUndoEventHandler = () => {
    setRedoableActions((prev) => prev + 1);
    setUndoableActions((prev) => Math.max(prev - 1, 0));
    readdHandlers(selectedTool);
  };

  const handleSketchRedo: __esri.SketchRedoEventHandler = () => {
    setUndoableActions((prev) => prev + 1);
    setRedoableActions((prev) => Math.max(prev - 1, 0));
    readdHandlers(selectedTool);
  };

  const handleToolSelect = (tool: SketchTool) => {
    if (!sketchRef.current) return;

    setSelectedTool(tool);
    switch (tool) {
      case SketchTool.Polygon:
      case SketchTool.Rectangle:
      case SketchTool.Circle: {
        const previousSelectedFeatureId = selectedFeatureId;
        resetGraphicSymbol(previousSelectedFeatureId, null);
        sketchRef.current.create(tool);
        break;
      }
      case SketchTool.Select:
      case SketchTool.Edit: {
        const previousSelectedFeatureId = selectedFeatureId;
        resetGraphicSymbol(previousSelectedFeatureId, null);
        // Cancel any active tool and just go into selection mode
        sketchRef.current.cancel();
        readdHandlers(tool);
        break;
      }
      default:
        break;
    }
  };

  const updateFeatureField = (
    field: LayerFieldDefinition,
    graphic: __esri.Graphic,
    e: React.ChangeEvent<HTMLInputElement>,
    graphicId: string,
  ) => {
    // Update the graphic directly
    e.stopPropagation();
    const newValue = e.target.value;
    graphic.attributes[field.customField.id] = newValue;
    markFeatureAsChangedDebounced?.(graphicId);
    setFeatureTracking((prev) => {
      const currentTracking = prev[graphicId] || {};
      return {
        ...prev,
        [graphicId]: {
          ...currentTracking,
          featureId: graphic.attributes.featureId,
          ...(currentTracking.isNew ? { isNew: true } : { isModified: true }),
        },
      };
    });
  };

  const deleteFeature = (e: any, tableFeatures: __esri.Collection<__esri.Graphic>, graphic: __esri.Graphic) => {
    e.stopPropagation();
    if (!sketchRef.current || !editLayerRef.current) return;
    const selectedGraphic = tableFeatures.find((g) => g.attributes?.graphicId === graphic.attributes?.graphicId);
    if (!selectedGraphic) return;
    markFeatureAsChanged?.(selectedGraphic.attributes.graphicId);
    sketchRef.current.cancel();
    sketchRef.current.update([selectedGraphic]).then(() => {
      sketchRef.current?.delete();
    });
  };

  // A more direct method to update a specific feature's property
  const updateFeatureProperty = (graphicId: string, fieldName: string, value: any) => {
    if (!editLayerRef.current) return;

    const graphics = editLayerRef.current.graphics;
    if (!graphicIndex.has(graphicId)) return false;
    const graphic = graphics.at(graphicIndex.get(graphicId)!);
    if (!graphic) return false;

    // Update the graphic attributes
    graphic.attributes = {
      ...graphic.attributes,
      [fieldName]: value,
    };

    // Mark as modified if it's an existing feature
    if (graphic.attributes.featureId) {
      setFeatureTracking((prev) => {
        const currentTracking = prev[graphicId] || {};
        if (currentTracking.isNew) return prev;
        return {
          ...prev,
          [graphicId]: {
            ...currentTracking,
            featureId: graphic.attributes.featureId,
            isModified: true,
          },
        };
      });
    }

    // Update selected feature properties if this is the selected feature
    if (graphicId === selectedFeatureId) {
      setFieldProperties((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
    }

    return true;
  };

  return {
    featureTracking,
    selectedFeatures,
    selectedFeatureId,
    fieldProperties,
    selectedTool,
    handleToolSelect,
    updateFeatureProperty,
    setFeatureTracking,
    setSelectedFeatureId,
    setFieldProperties,
    updateFeatureField,
    deleteFeature,
    maxFeatureIndex,
    showFeaturePopup,
    setShowFeaturePopup,
    readdHandlers,
    sketchCanUndo: undoableActions > 0,
    sketchCanRedo: redoableActions > 0,
  };
}
